import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RefreshIcon } from 'assets/icons';
import MasterFilter, {
  type DateRangeValue,
  type FilterFieldConfig,
  useMasterFilter,
} from 'components/common/MasterFilter';
import { useLeadFilterOptions } from 'components/common/MasterFilter/useLeadFilterOptions';
import moment from 'moment';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetBusinessUnitBranchOfficesQuery, useGetBusinessUnitQuery } from 'services/api/businessUnits.api';
import {
  useGetBankOfferMetricsMutation,
  useGetLeadMetricsMutation,
  useGetResponseTimesMetricsMutation,
} from 'services/api/insights.api';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import buildLeadsFilterUrl from 'utils/buildLeadsFilterUrl';

import getLeadSmallMetrics from '../utils/metricsGetters/getLeadSmallMetrics';
import BankOffersFunnel from './BankOffersFunnel';
import BulletGraph from './BulletGraph';
import DashboardHero from './DashboardHero';
import HardMetricCard from './HardMetricCard';
import LeadCharts from './LeadCharts';
import LeadsSankey from './LeadsSankey';

interface MetricData {
  type: string;
  data: number;
  secondaryData?: number;
  tooltip: string;
  percentage?: boolean;
  onActionButtonClick?: () => void;
  navigationTooltip?: string;
}

interface LeadsDashboardProps {
  businessUnitId: number;
}

const BUSINESS_UNIT_TYPE_KEY = '_businessUnitType';

const formatDateRangeLabel = (start?: moment.Moment | null, end?: moment.Moment | null): string => {
  if (!start || !end) return '';
  const fmt = (m: moment.Moment) => m.locale('es').format('D MMM');
  const sameYear = start.year() === end.year();
  const yearSuffix = sameYear ? `, ${end.year()}` : '';
  return `${fmt(start)} – ${fmt(end)}${yearSuffix}`;
};

const LeadsDashboard: React.FC<LeadsDashboardProps> = ({ businessUnitId }) => {
  const { data: businessUnitData } = useGetBusinessUnitQuery(businessUnitId, {
    skip: businessUnitId === -1,
  });
  const [getLeadMetrics, { data: leadMetrics, isLoading: isLeadMetricsLoading }] = useGetLeadMetricsMutation();
  const [getResponseTimesMetrics, { data: responseTimesMetrics }] = useGetResponseTimesMetricsMutation();
  const [
    getBankOfferMetrics,
    { data: bankOfferMetrics, isLoading: isBankOfferMetricsLoading, isError: isBankOfferMetricsError },
  ] = useGetBankOfferMetricsMutation();

  // Drives whether the "Sucursal" filter is shown at all — a BU with no branch offices
  // configured (getBranchOfficesList) gets no filter rather than an empty dropdown.
  const { data: branchOfficeOptions } = useGetBusinessUnitBranchOfficesQuery(
    { businessUnitId },
    { skip: businessUnitId === -1 }
  );
  const hasBranchOffices = (branchOfficeOptions?.length ?? 0) > 0;

  const dashboardFilterConfig = useMemo<FilterFieldConfig[]>(
    () => [
      {
        key: 'dateRange',
        kind: 'dateRange',
        label: 'Fecha',
        placement: 'bar',
        defaultValue: {
          start: moment().startOf('month'),
          end: moment().endOf('day'),
        } satisfies DateRangeValue,
      },
      { key: 'sources', kind: 'multiSelect', label: 'Origen', placement: 'bar' },
      {
        key: 'brands',
        kind: 'multiSelect',
        label: 'Marca',
        placement: 'bar',
        visibleWhen: (values) => values[BUSINESS_UNIT_TYPE_KEY] === 'car_dealership',
      },
      { key: 'origins', kind: 'multiSelect', label: 'Categoría', placement: 'bar' },
      { key: 'subOrigins', kind: 'multiSelect', label: 'Subcategoría', placement: 'bar' },
      ...(hasBranchOffices
        ? [{ key: 'branchOffices', kind: 'multiSelect' as const, label: 'Sucursal', placement: 'bar' as const }]
        : []),
    ],
    [hasBranchOffices]
  );

  const filter = useMasterFilter(dashboardFilterConfig, businessUnitId);
  const { values, setValue } = filter;
  const [searchParams] = useSearchParams();
  const urlParamsApplied = useRef(false);

  const dateRange = (values.dateRange as DateRangeValue | undefined) ?? { start: null, end: null };
  const initialDate = dateRange.start;
  const endDate = dateRange.end;
  const sources = (values.sources as string[] | undefined) ?? [];
  const origins = (values.origins as string[] | undefined) ?? [];
  const subOrigins = (values.subOrigins as string[] | undefined) ?? [];
  const brands = (values.brands as string[] | undefined) ?? [];
  const branchOffices = (values.branchOffices as string[] | undefined) ?? [];

  const dynamicOptions = useLeadFilterOptions(
    businessUnitId,
    initialDate?.toISOString() ?? null,
    endDate?.toISOString() ?? null,
    'lead'
  );

  const businessUnitType = businessUnitData?.type ?? null;
  const isFinancialInstitutionBU = businessUnitType === 'financial_institution';
  const filterBusinessUnitType = values[BUSINESS_UNIT_TYPE_KEY] ?? null;
  useEffect(() => {
    if (filterBusinessUnitType !== businessUnitType) {
      setValue(BUSINESS_UNIT_TYPE_KEY, businessUnitType);
    }
  }, [businessUnitType, filterBusinessUnitType, setValue]);

  useEffect(() => {
    if (urlParamsApplied.current) return;
    const startParam = searchParams.get('startDate');
    const endParam = searchParams.get('endDate');
    const sourceParam = searchParams.get('source');
    if (!startParam && !endParam && !sourceParam) return;
    if (startParam || endParam) {
      // Snap to full-day bounds (start 00:00:00, end 23:59:59.999) so the query includes
      // both the opening and the closing date regardless of whether the range is "today".
      setValue('dateRange', {
        start: startParam ? moment(startParam, 'YYYY-MM-DD').startOf('day') : null,
        end: endParam ? moment(endParam, 'YYYY-MM-DD').endOf('day') : null,
      });
    }
    if (sourceParam) {
      setValue('sources', [sourceParam]);
    }
    urlParamsApplied.current = true;
  }, [businessUnitId]);

  const handleNavigateToLeads = (url: string): void => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const fetchAllMetrics = useCallback(() => {
    if (businessUnitId === -1 || !initialDate || !endDate) return;
    const payload = {
      startDate: initialDate.toISOString(),
      endDate: endDate.toISOString(),
      businessUnitId,
      sources,
      origins,
      subOrigins,
      brands,
      branchOffices,
    };
    getLeadMetrics(payload).catch((err) => console.error(err));
    getResponseTimesMetrics(payload).catch((err) => console.error(err));
    // The bank offer funnel only applies to the financial-institution vertical; the other lead
    // filters (marca, categoría) describe the lead, not the offer, so they are not forwarded.
    if (isFinancialInstitutionBU) {
      getBankOfferMetrics({
        startDate: payload.startDate,
        endDate: payload.endDate,
        businessUnitId,
        sources,
      }).catch((err) => console.error(err));
    }
  }, [
    businessUnitId,
    initialDate,
    endDate,
    sources,
    origins,
    subOrigins,
    brands,
    branchOffices,
    isFinancialInstitutionBU,
    getLeadMetrics,
    getResponseTimesMetrics,
    getBankOfferMetrics,
  ]);

  const startMs = initialDate?.valueOf();
  const endMs = endDate?.valueOf();
  const sourcesKey = JSON.stringify(sources);
  const originsKey = JSON.stringify(origins);
  const subOriginsKey = JSON.stringify(subOrigins);
  const brandsKey = JSON.stringify(brands);
  const branchOfficesKey = JSON.stringify(branchOffices);
  useEffect(() => {
    const timer = setTimeout(fetchAllMetrics, 400);
    return () => clearTimeout(timer);
  }, [
    businessUnitId,
    startMs,
    endMs,
    sourcesKey,
    originsKey,
    subOriginsKey,
    brandsKey,
    branchOfficesKey,
    isFinancialInstitutionBU,
  ]);

  const filtersForCards = useMemo(
    () => ({
      startDate: initialDate?.toDate() ?? new Date(),
      endDate: endDate?.toDate() ?? new Date(),
      businessUnitId,
      sources: sources.length > 0 ? sources : [],
      brands: brands.length > 0 ? brands : [],
      origins: origins.length > 0 ? origins : [],
      subOrigins: subOrigins.length > 0 ? subOrigins : [],
      branchOffices: branchOffices.length > 0 ? branchOffices : [],
    }),
    [initialDate, endDate, businessUnitId, sources, brands, origins, subOrigins, branchOffices]
  );

  const smallHardMetrics = getLeadSmallMetrics({
    businessUnitLeadMetrics: leadMetrics || null,
    totalTemplateMessages: responseTimesMetrics?.hardMetrics?.totalTemplateMessages,
    onActionButtonClick: handleNavigateToLeads,
    filters: filtersForCards,
    hasSellerManagement: businessUnitData?.hasSellerManagement,
  });

  // ─── Derived hero & flow values ────────────────────────────────────────────
  const totalLeads = leadMetrics?.hardMetrics?.leadsCount ?? 0;
  const leadsContacted = leadMetrics?.hardMetrics?.leadsContacted ?? 0;
  const leadsAnswered = leadMetrics?.hardMetrics?.leadsThatAnsweredCount ?? 0;
  const leadsInCRM = leadMetrics?.hardMetrics?.leadsUploadedToCRM ?? 0;
  const leadsWithVisitDate = leadMetrics?.hardMetrics?.leadsWithVisitDate ?? 0;
  // Present only for BUSINESS_UNITS_WITH_INTERESTED_CRM_LEADS (backend gates the fields).
  const interestedCRM = leadMetrics?.hardMetrics?.interestedLeadsUploadedToCRM;
  const notInterestedCRM = leadMetrics?.hardMetrics?.notInterestedLeadsUploadedToCRM;
  const showInterestedBreakdown = interestedCRM != null && notInterestedCRM != null;

  const dailyContacted = useMemo(() => Object.values(leadMetrics?.chartsData?.leadsByCreationDay ?? {}), [leadMetrics]);
  const dailyCRM = useMemo(
    () => Object.values(leadMetrics?.chartsData?.leadsUploadedToCRMByCreationDay ?? {}),
    [leadMetrics]
  );

  const periodLabel = formatDateRangeLabel(initialDate, endDate);

  const crmShare = leadsContacted > 0 ? leadsInCRM / leadsContacted : 0;
  const contactShare = totalLeads > 0 ? leadsContacted / totalLeads : 0;
  const responseShare = leadsContacted > 0 ? leadsAnswered / leadsContacted : 0;
  const visitShare = leadsContacted > 0 ? leadsWithVisitDate / leadsContacted : 0;
  const showVisitMetric = businessUnitData?.type === 'car_dealership';

  const interestedPct = showInterestedBreakdown && leadsInCRM > 0 ? ((interestedCRM ?? 0) / leadsInCRM) * 100 : 0;
  const notInterestedPct = showInterestedBreakdown && leadsInCRM > 0 ? ((notInterestedCRM ?? 0) / leadsInCRM) * 100 : 0;

  const PctEm: React.FC<{ value: string }> = ({ value }) => (
    <Box component="span" sx={{ color: NAVY, fontWeight: 700, fontSize: '1.15rem', letterSpacing: '-0.01em' }}>
      {value}
    </Box>
  );

  const CaptionEm: React.FC<{ value: string }> = ({ value }) => (
    <Box component="span" sx={{ color: NAVY, fontWeight: 700, fontSize: '0.95rem', letterSpacing: '-0.01em' }}>
      {value}
    </Box>
  );

  const inlineNavBtnSx = {
    ml: 0.25,
    width: 22,
    height: 22,
    color: `${NAVY}80`,
    backgroundColor: 'transparent',
    transition: 'color 0.15s ease, background-color 0.15s ease',
    '&:hover': { color: NAVY, backgroundColor: `${NAVY}0d` },
  } as const;

  const heroContext =
    totalLeads > 0 ? (
      <>
        <Box>
          <PctEm value={`${(crmShare * 100).toFixed(1)}%`} /> de contactados
          {periodLabel ? ` (${periodLabel})` : ''}.
        </Box>
        {showInterestedBreakdown && (
          <>
            <Box sx={{ mt: 0.75 }}>
              Enviados como interesados: <strong>{(interestedCRM ?? 0).toLocaleString()}</strong> (
              <PctEm value={`${interestedPct.toFixed(1)}%`} /> del total CRM)
              <Tooltip title="Ver leads enviados como interesados" arrow>
                <IconButton
                  size="small"
                  onClick={() =>
                    handleNavigateToLeads(
                      buildLeadsFilterUrl({ ...filtersForCards, interestedCRMLeads: true, viewMode: 'table' })
                    )
                  }
                  aria-label="Ver leads enviados como interesados"
                  sx={inlineNavBtnSx}
                >
                  <ManageSearchIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ mt: 0.25 }}>
              Enviados como no interesados: <strong>{(notInterestedCRM ?? 0).toLocaleString()}</strong> (
              <PctEm value={`${notInterestedPct.toFixed(1)}%`} /> del total CRM)
              <Tooltip title="Ver leads enviados como no interesados" arrow>
                <IconButton
                  size="small"
                  onClick={() =>
                    handleNavigateToLeads(
                      buildLeadsFilterUrl({ ...filtersForCards, notInterestedCRMLeads: true, viewMode: 'table' })
                    )
                  }
                  aria-label="Ver leads enviados como no interesados"
                  sx={inlineNavBtnSx}
                >
                  <ManageSearchIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
      </>
    ) : undefined;

  return (
    <Box>
      <Box>
        <MasterFilter {...filter} dynamicOptions={dynamicOptions} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1.5 }}>
        <Tooltip title="Actualizar métricas" arrow>
          <span>
            <IconButton
              onClick={fetchAllMetrics}
              disabled={isLeadMetricsLoading || !initialDate || !endDate}
              size="small"
              aria-label="Actualizar métricas"
              sx={{
                color: alpha(NAVY, 0.55),
                transition: 'all 0.15s ease-in-out',
                '&:hover': { bgcolor: alpha(NAVY, 0.06) },
              }}
            >
              {isLeadMetricsLoading ? (
                <CircularProgress size={16} sx={{ color: NAVY }} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {!leadMetrics ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            mt: 4,
            py: { xs: 8, md: 10 },
            backgroundColor: SURFACE_CONTAINER_LOW,
            borderRadius: RADIUS_COIN,
            textAlign: 'center',
            px: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.1rem', md: '1.35rem' },
              color: NAVY,
              maxWidth: 480,
            }}
          >
            Aún no hay métricas para este período
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              color: alpha(NAVY, 0.6),
              fontSize: '0.95rem',
              mt: 1,
              maxWidth: 480,
            }}
          >
            Ajusta el rango de fechas o cambia los filtros para ver el flujo de leads.
          </Typography>
        </Box>
      ) : (
        <>
          {isLeadMetricsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: NAVY }} />
            </Box>
          ) : (
            <>
              {/* ─── OMTM hero ─────────────────────────────────────────── */}
              <DashboardHero
                metricLabel="Métrica principal"
                headline="Leads enviados al CRM"
                value={leadsInCRM.toLocaleString()}
                sparkline={dailyCRM}
                context={heroContext}
                onNavigate={() =>
                  handleNavigateToLeads(
                    buildLeadsFilterUrl({ ...filtersForCards, CRMUpdateAt: true, viewMode: 'table' })
                  )
                }
                supporting={
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gridAutoRows: '1fr',
                      gap: 1.5,
                    }}
                  >
                    <HardMetricCard
                      type="Total de leads"
                      data={totalLeads}
                      tooltip="Leads recibidos en el período."
                      sparkline={dailyContacted}
                      inlineSparkline
                      onActionButtonClick={() =>
                        handleNavigateToLeads(buildLeadsFilterUrl({ ...filtersForCards, viewMode: 'table' }))
                      }
                      navigationTooltip="Ver todos los leads"
                    />
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: SURFACE_CONTAINER_LOWEST,
                        borderRadius: RADIUS_COIN,
                        boxShadow: SHADOW,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <BulletGraph
                        label="Contactados"
                        value={contactShare}
                        max={1}
                        bands={[0.5, 0.75]}
                        format={(v) => `${(v * 100).toFixed(0)}%`}
                        caption={
                          <>
                            <CaptionEm value={leadsContacted.toLocaleString()} /> de {totalLeads.toLocaleString()} leads
                            recibidos
                          </>
                        }
                        onNavigate={() =>
                          handleNavigateToLeads(
                            buildLeadsFilterUrl({ ...filtersForCards, leadsContacted: true, viewMode: 'table' })
                          )
                        }
                        navigationTooltip="Ver leads contactados"
                      />
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: SURFACE_CONTAINER_LOWEST,
                        borderRadius: RADIUS_COIN,
                        boxShadow: SHADOW,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <BulletGraph
                        label="Respondieron"
                        value={responseShare}
                        max={1}
                        bands={[0.15, 0.3]}
                        format={(v) => `${(v * 100).toFixed(0)}%`}
                        caption={
                          <>
                            <CaptionEm value={leadsAnswered.toLocaleString()} /> respondieron de{' '}
                            {leadsContacted.toLocaleString()} contactados
                          </>
                        }
                        onNavigate={() =>
                          handleNavigateToLeads(
                            buildLeadsFilterUrl({
                              ...filtersForCards,
                              leadsContacted: true,
                              hasClientMessages: true,
                              viewMode: 'table',
                            })
                          )
                        }
                        navigationTooltip="Ver leads que respondieron"
                      />
                    </Box>
                    {showVisitMetric && (
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: SURFACE_CONTAINER_LOWEST,
                          borderRadius: RADIUS_COIN,
                          boxShadow: SHADOW,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <BulletGraph
                          label="Agendaron visita"
                          value={visitShare}
                          max={1}
                          bands={[0.05, 0.15]}
                          format={(v) => `${(v * 100).toFixed(0)}%`}
                          caption={
                            <>
                              <CaptionEm value={leadsWithVisitDate.toLocaleString()} /> agendaron de{' '}
                              {leadsContacted.toLocaleString()} contactados
                            </>
                          }
                          onNavigate={() =>
                            handleNavigateToLeads(
                              buildLeadsFilterUrl({ ...filtersForCards, visitDate: true, viewMode: 'table' })
                            )
                          }
                          navigationTooltip="Ver leads que agendaron visita"
                        />
                      </Box>
                    )}
                  </Box>
                }
              />

              {/* ─── Sankey flow ───────────────────────────────────────── */}
              <LeadsSankey
                totalLeads={leadMetrics.hardMetrics.leadsCount || 0}
                leadsContacted={leadMetrics.hardMetrics.leadsContacted || 0}
                leadsFailed={leadMetrics.hardMetrics.leadsThatFailedToBeContacted || 0}
                leadsDiscarded={leadMetrics.hardMetrics.leadsDiscarded || 0}
                leadsPending={leadMetrics.hardMetrics.pendingLeads || 0}
                leadsResponded={leadMetrics.hardMetrics.leadsThatAnsweredCount || 0}
                leadsInCRM={leadMetrics.hardMetrics.leadsUploadedToCRM || 0}
                respondedLeadsInCRM={leadMetrics.hardMetrics.respondedLeadsUploadedToCRM || 0}
                notRespondedLeadsInCRM={leadMetrics.hardMetrics.notRespondedLeadsUploadedToCRM || 0}
                failedLeadsInCRM={leadMetrics.hardMetrics.failedLeadsUploadedToCRM || 0}
                discardedLeadsInCRM={leadMetrics.hardMetrics.discardedLeadsUploadedToCRM || 0}
                pendingLeadsInCRM={leadMetrics.hardMetrics.pendingLeadsUploadedToCRM || 0}
                failedLeadsByReason={leadMetrics.hardMetrics.failedLeadsByReason}
                failedLeadsByReasonCRM={leadMetrics.hardMetrics.failedLeadsByReasonCRM}
                filters={{
                  startDate: initialDate?.toDate() ?? new Date(),
                  endDate: endDate?.toDate() ?? new Date(),
                  businessUnitId,
                  sources: sources.length > 0 ? sources : undefined,
                  brands: brands.length > 0 ? brands : undefined,
                }}
                onNavigateToLeads={handleNavigateToLeads}
              />

              {/* ─── Bank offer funnel (financial institutions only) ───── */}
              {isFinancialInstitutionBU && (
                <BankOffersFunnel
                  metrics={bankOfferMetrics}
                  isLoading={isBankOfferMetricsLoading}
                  hasError={isBankOfferMetricsError}
                  onRetry={fetchAllMetrics}
                  periodLabel={periodLabel}
                  onNavigate={(bankOfferStatus) =>
                    handleNavigateToLeads(
                      buildLeadsFilterUrl({ ...filtersForCards, bankOfferStatus, viewMode: 'table' })
                    )
                  }
                />
              )}

              {/* ─── Secondary metric strip ────────────────────────────── */}
              {smallHardMetrics.length > 0 && (
                <Grid container sx={{ my: 1 }}>
                  {smallHardMetrics.map((metric: MetricData) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 'grow' }} key={metric.type} sx={{ p: 1 }}>
                      <HardMetricCard {...metric} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}

          <LeadCharts
            leadMetrics={leadMetrics}
            responseTimesMetrics={responseTimesMetrics}
            businessUnitId={businessUnitId}
            businessUnitType={businessUnitData?.type ?? undefined}
            onNavigateToLeads={handleNavigateToLeads}
            filters={{
              startDate: initialDate?.toDate() ?? new Date(),
              endDate: endDate?.toDate() ?? new Date(),
              businessUnitId,
              sources: sources.length > 0 ? sources : undefined,
              brands: brands.length > 0 ? brands : undefined,
            }}
          />
        </>
      )}
    </Box>
  );
};

export default LeadsDashboard;
